import type { AssignmentDetail, AssignmentResponseWithDetails } from '@repo/types/assignments';

import { useGetAssignment } from '@repo/api/assignment/useGetAssignment';
import { useGetAssignmentSubmissions } from '@repo/api/assignment/useGetAssignmentSubmissions';
import { format } from 'date-fns';
import { ArrowLeftIcon, ChevronDownIcon, CircleSlash, EyeIcon, PencilLineIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';

import { AssignmentFeedbackForm } from '@/components/features/assignments/AssignmentFeedbackForm';
import { AssignmentGradeForm } from '@/components/features/assignments/AssignmentGradeForm';
import { AssignmentBuilderQuizCard } from '@/components/features/assignments/builder/AssignmentBuilderQuizCard';
import { AttachmentCard } from '@/components/features/files/attachment-card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button, ButtonGroup } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateFooter, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { Tiptap } from '@/components/ui/tiptap-editor';
import { postExtensionSet } from '@/lib/tiptap-extensions-sets';
import { getAvatar } from '@/utils/getAvatar/getAvatar';

export default function ManageAssignmentGradePage() {
  const navigate = useNavigate();
  const { assignmentId: assignmentIdParam, responseId: responseIdParam } = useParams<{ assignmentId?: string; responseId?: string }>();
  const assignmentId = assignmentIdParam ? Number(assignmentIdParam) : undefined;
  const responseId = responseIdParam ? Number(responseIdParam) : undefined;
  const isValidId = assignmentId !== undefined && !Number.isNaN(assignmentId) && responseId !== undefined && !Number.isNaN(responseId);

  const assignmentQuery = useGetAssignment(isValidId ? assignmentId : undefined);
  const submissionsQuery = useGetAssignmentSubmissions(isValidId ? assignmentId : undefined);

  const assignment = assignmentQuery.data;
  const submission = useMemo(() => submissionsQuery.data?.find((entry) => entry.id === responseId), [submissionsQuery.data, responseId]);

  if (!isValidId) {
    return <div className="text-muted-foreground flex h-full w-full items-center justify-center p-4">Nieprawidłowy adres.</div>;
  }

  if (assignmentQuery.isError) {
    return (
      <div className="base-direction-col">
        <EmptyState>
          <EmptyStateCircles className="z-0" />
          <FeaturedIcon size="lg" className="z-10">
            <CircleSlash />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>{assignmentQuery.errorMessage.title}</EmptyStateTitle>
            <EmptyStateDescription>{assignmentQuery.errorMessage.detail}</EmptyStateDescription>
          </EmptyStateContent>
          <EmptyStateFooter>
            <Button variant="secondary" onClick={() => navigate(-1)}>
              <ArrowLeftIcon />
              Powrót
            </Button>
          </EmptyStateFooter>
        </EmptyState>
      </div>
    );
  }

  if (submissionsQuery.isPending || assignmentQuery.isPending) {
    return (
      <div className="base-direction-col">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!assignment || !submission) {
    return (
      <div className="base-direction-col">
        <EmptyState>
          <EmptyStateCircles className="z-0" />
          <FeaturedIcon size="lg" className="z-10">
            <CircleSlash />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>Nie znaleziono odpowiedzi</EmptyStateTitle>
            <EmptyStateDescription>Ta odpowiedź nie istnieje lub została usunięta.</EmptyStateDescription>
          </EmptyStateContent>
          <EmptyStateFooter>
            <Button variant="secondary" asChild>
              <Link to={`/manage/assignments/${assignmentId}`}>
                <ArrowLeftIcon />
                Powrót do zadania
              </Link>
            </Button>
          </EmptyStateFooter>
        </EmptyState>
      </div>
    );
  }

  return <AssignmentGradeContent key={submission.id} assignmentId={assignmentId} assignment={assignment} submission={submission} />;
}

interface AssignmentGradeContentProps {
  assignmentId: number;
  assignment: AssignmentDetail;
  submission: AssignmentResponseWithDetails;
}

function AssignmentGradeContent({ assignmentId, assignment, submission }: AssignmentGradeContentProps) {
  const { pathname, search } = useLocation();
  return (
    <div className="base-direction-col h-full overflow-hidden">
      <div className="bg-background border-border-primary viewport-height flex w-full flex-col overflow-hidden rounded-2xl border">
        {/* Header */}
        <header className="border-border-primary flex flex-col gap-4 border-b p-4">
          <Button variant="ghost" size="sm" className="-ml-2 w-fit" asChild>
            <Link to={`/manage/assignments/${assignmentId}`}>
              <ArrowLeftIcon />
              Powrót do zadania
            </Link>
          </Button>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-col gap-2">
              <h1 className="text-lg font-semibold">{assignment.name}</h1>
              <div className="flex items-center gap-2">
                <Avatar size="sm">
                  <AvatarImage src={getAvatar(submission.user.avatarURL)} />
                  <AvatarFallback>
                    {submission.user.name.charAt(0)}
                    {submission.user.surname.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-muted-foreground text-sm">
                  {submission.user.name} {submission.user.surname} · Przesłano {format(submission.createdAt, 'dd.MM.yyyy HH:mm')}
                </span>
              </div>
            </div>

            {!assignment.quiz && <AssignmentGradeForm assignmentId={assignmentId} assignment={assignment} submission={submission} />}
          </div>
        </header>

        {/* Body */}
        <div className="grid flex-1 grid-cols-1 gap-0 overflow-auto lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
          {/* Left: assignment description + student answer + grading form */}
          <div className="lg:border-border-primary flex flex-col gap-6 p-4 lg:border-r">
            {assignment.desc && (
              <section className="flex flex-col gap-2">
                <p className="text-muted-foreground text-sm font-medium">Treść zadania</p>
                <Tiptap extensions={postExtensionSet} editable={false} content={assignment.desc} />
              </section>
            )}

            <section className="border-border-primary bg-accent flex flex-col gap-2 rounded-lg border p-3">
              <p className="text-muted-foreground text-sm font-medium">Odpowiedź</p>
              {submission.body ? <Tiptap extensions={postExtensionSet} editable={false} content={submission.body} /> : <p className="text-muted-foreground text-sm italic">Brak treści odpowiedzi.</p>}
            </section>

            {!assignment.quiz && <AssignmentFeedbackForm assignmentId={assignmentId} submission={submission} />}
          </div>

          {/* Right: quiz preview + assignment attachments + submitted files */}
          <div className="flex flex-col gap-6 p-4">
            {assignment.quiz && (
              <section className="flex flex-col gap-3">
                <p className="text-muted-foreground text-sm font-medium">Quiz</p>
                <AssignmentBuilderQuizCard
                  quiz={assignment.quiz}
                  badge={
                    submission.gradeId == null ? (
                      <Badge variant="destructive" border>
                        Nieoceniono
                      </Badge>
                    ) : (
                      <Badge variant="success" border>
                        Oceniono
                      </Badge>
                    )
                  }
                  actions={
                    submission.quizRespId ? (
                      <ButtonGroup>
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/manage/quizzes/${assignment.quiz.id}/attempt/${submission.quizRespId}/mark`}>
                            <PencilLineIcon />
                            Oceń quiz
                          </Link>
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" aria-label="Więcej opcji" className="group px-2">
                              <ChevronDownIcon className="transition-transform duration-200 group-data-[state=open]:rotate-180" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuGroup>
                              <DropdownMenuItem asChild>
                                <Link to={`/manage/quizzes/${assignment.quiz.id}/attempt/${submission.quizRespId}/mark`}>
                                  <PencilLineIcon />
                                  Oceń quiz
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link to={`/quizzes/${assignment.quiz.id}/attempt/${submission.quizRespId}/result?backTo=${encodeURIComponent(pathname + search)}`}>
                                  <EyeIcon />
                                  Zobacz podejście
                                </Link>
                              </DropdownMenuItem>
                            </DropdownMenuGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </ButtonGroup>
                    ) : undefined
                  }
                />
                {!submission.quizRespId && <p className="text-muted-foreground text-xs italic">Brak podejścia do quizu.</p>}
              </section>
            )}

            <section className="flex flex-col gap-3">
              <p className="text-muted-foreground text-sm font-medium">Załączniki do zadania:</p>
              {assignment.files.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {assignment.files.map((file) => (
                    <AttachmentCard key={file.id} id={file.id} name={file.name} className="min-w-[200px] flex-1" />
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-xs italic">Brak załączonych plików.</p>
              )}
            </section>

            <section className="flex flex-col gap-3">
              <p className="text-muted-foreground text-sm font-medium">Przesłane pliki</p>
              {submission.files.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {submission.files.map((file) => (
                    <AttachmentCard key={file.id} id={file.id} name={file.name} className="min-w-[200px] flex-1" />
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-xs italic">Brak przesłanych plików.</p>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
